import { TSelectOption } from 'src/app/project/components/input/select/select.model';
import { translate } from 'src/app/project/features/translate/translate';
import { EIntegrationStatus } from 'src/app/project/modules/custom-fields/custom-fields.model';
import { checkCustomWorkspaceId } from 'src/app/project/modules/workspace/workspace';
import { TWorkspace } from 'src/app/project/modules/workspace/workspace.model';
import { EWorkspaces } from 'src/app/project/modules/workspace/workspaces.enum';
import { EBasicField, EBasicFieldShort } from 'src/app/project/shared/enums/basic-fields-enums';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { ECustomFieldType } from '../../../../custom-fields/custom-field-types-enums';
import { GET_CUSTOM_FIELDS } from '../../../../custom-fields/custom-fields.store';
import { TColumn } from '../../../../site/site-table/columns/column.model';
import { GET_COLUMNS } from '../../../../site/site-table/columns/columns.store';
import { EAdvancedFilterType } from '../../../models/advanced-filter-type.enum';

export function getAdvancedFilterFields(
  columns: TColumn[],
  workspace: TWorkspace,
): TSelectOption[] {
  const isSiteWithSitePlan = workspace?.siteImageRef?.id ? true : false;
  const isCustomWorkspace = !workspace && checkCustomWorkspaceId() === EWorkspaces.OVERVIEW;

  const fields: TSelectOption[] = columns.map((column) => {
    const customFieldIds = column.customFieldIds
      ? column.customFieldIds
      : column.workspaceId
        ? [column.workspaceId]
        : [];

    return {
      value: column.name,
      label: column.name,
      iconSrc: searchNewValue(column.name, customFieldIds)?.iconSrc,
      tooltip: searchNewValue(column.name, customFieldIds)?.tooltip,
      customFields: customFieldIds,
    };
  });

  if (isSiteWithSitePlan || isCustomWorkspace) {
    fields.unshift({
      value: EAdvancedFilterType.LOCATION,
      label: translate('location'),
      iconSrc: EIconPath.ICON_ACTIVITIES_LOCATION_14,
    });
  }

  return fields;
}

export function getFilterType(customFieldType: ECustomFieldType): {
  type: EAdvancedFilterType;
  iconSrc: EIconPath;
} {
  switch (customFieldType) {
    case ECustomFieldType.TEXT:
      return { type: EAdvancedFilterType.TEXT, iconSrc: EIconPath.ICON_FIELDS_TEXT_14 };
    case ECustomFieldType.LIST:
      return { type: EAdvancedFilterType.LIST, iconSrc: EIconPath.ICON_FIELDS_LIST_14 };
    case ECustomFieldType.DATE:
      return { type: EAdvancedFilterType.DATE, iconSrc: EIconPath.ICON_FIELDS_DATE_14 };
    case ECustomFieldType.COST:
      return { type: EAdvancedFilterType.COST, iconSrc: EIconPath.ICON_FIELDS_COST_14 };
    case ECustomFieldType.NUMBERS:
      return { type: EAdvancedFilterType.NUMBER, iconSrc: EIconPath.ICON_FIELDS_NUMBERS_14 };
    case ECustomFieldType.RICHTEXT:
      return { type: EAdvancedFilterType.RICH_TEXT, iconSrc: EIconPath.ICON_FIELDS_RICH_TEXT_14 };
    case ECustomFieldType.TIME:
      return { type: EAdvancedFilterType.DURATION, iconSrc: EIconPath.ICON_FIELDS_TIME_14 };
    case ECustomFieldType.PERCENTAGE:
      return { type: EAdvancedFilterType.PERCENTAGE, iconSrc: EIconPath.ICON_FIELDS_PERCENTAGE_14 };
    case ECustomFieldType.CHECKBOX:
      return { type: EAdvancedFilterType.CHECKBOX, iconSrc: EIconPath.ICON_FIELDS_CHECKBOX_14 };
    case ECustomFieldType.TIMELINE:
      return { type: EAdvancedFilterType.TIMELINE, iconSrc: EIconPath.ICON_FIELDS_TIMELINE_14 };
    case ECustomFieldType.FORMULA:
      return { type: EAdvancedFilterType.FORMULA, iconSrc: EIconPath.ICON_FIELDS_FORMULA_14 };
    case ECustomFieldType.MULTI_LIST:
      return { type: EAdvancedFilterType.MULTI_LIST, iconSrc: EIconPath.ICON_FIELDS_MULTI_LIST_14 };

    default:
      return null;
  }
}

export function filterIdToColumn(id: string): TColumn {
  const columns = GET_COLUMNS();

  switch (id) {
    case null:
    case undefined:
      return null;
    case 'workspaceRef.id':
      return {
        name: EBasicField.ACCOUNT_SITE,
      };
    case EBasicField.ACCOUNT:
      return columns.find((column) => column.name === EBasicField.ACCOUNT);
    case EBasicField.SITE:
      return columns.find((column) => column.name === EBasicField.SITE);
    case EBasicField.PRIORITY:
      return columns.find((column) => column.name === EBasicField.PRIORITY);
    case EBasicField.STATUS:
      return columns.find((column) => column.name === EBasicField.STATUS);
    case EBasicField.ASSIGNEES:
      return columns.find((column) => column.name === EBasicField.ASSIGNEES);
    case EBasicField.TITLE:
      return columns.find((column) => column.name === EBasicField.TITLE);
    case EBasicField.DESCRIPTION:
      return columns.find((column) => column.name === EBasicField.DESCRIPTION);
    case EBasicField.CREATED:
      return columns.find((column) => column.name === EBasicField.CREATED);
    case EBasicField.UPDATED:
      return columns.find((column) => column.name === EBasicField.UPDATED);
    case EBasicField.CREATED_BY:
      return columns.find((column) => column.name === EBasicField.CREATED_BY);
    case EBasicField.TAGS:
      return columns.find((column) => column.name === EBasicField.TAGS);
    case EBasicField.SEQUENCE_NUMBER:
    case EBasicFieldShort.SEQUENCE_NUMBER:
      return columns.find((column) => column.name === EBasicFieldShort.SEQUENCE_NUMBER);

    case EAdvancedFilterType.LOCATION:
      return {
        name: EAdvancedFilterType.LOCATION,
      };
    case EAdvancedFilterType.REMINDER:
      return {
        name: EAdvancedFilterType.REMINDER,
      };
    case EAdvancedFilterType.REMINDER_TYPE:
      return {
        name: EAdvancedFilterType.REMINDER_TYPE,
      };
    case EAdvancedFilterType.REMINDER_STATE:
      return {
        name: EAdvancedFilterType.REMINDER_STATE,
      };
    case EAdvancedFilterType.REMINDER_CREATED_BY:
      return {
        name: EAdvancedFilterType.REMINDER_CREATED_BY,
      };
    case EAdvancedFilterType.FLAGGED:
      return columns.find((column) => column.name === EBasicFieldShort.FLAGGED);
    default: {
      const customFields = GET_CUSTOM_FIELDS();
      const field = Object.values(customFields).find((item) => item.id === id);

      return columns.find((column) => column.name === field.label);
    }
  }
}

export function searchNewValue(
  newValue: string,
  customFieldIds: string[],
): {
  id: EBasicField | string;
  type: EAdvancedFilterType;
  iconSrc: EIconPath;
  tooltip: string;
} {
  switch (newValue) {
    case EBasicField.ACCOUNT_SITE:
      return {
        id: 'workspaceRef.id',
        type: EAdvancedFilterType.ACCOUNT_SITE,
        iconSrc: EIconPath.ICON_FIELDS_TEXT_14,
        tooltip: '',
      };
    case EBasicField.ACCOUNT:
      return {
        id: EBasicField.ACCOUNT,
        type: EAdvancedFilterType.ACCOUNT,
        iconSrc: EIconPath.ICON_FIELDS_TEXT_14,
        tooltip: '',
      };
    case EBasicField.SITE:
      return {
        id: EBasicField.SITE,
        type: EAdvancedFilterType.SITE,
        iconSrc: EIconPath.ICON_FIELDS_TEXT_14,
        tooltip: '',
      };
    case EBasicField.PRIORITY:
      return {
        id: EBasicField.PRIORITY,
        type: EAdvancedFilterType.PRIORITY,
        iconSrc: EIconPath.ICON_ACTIVITIES_PRIORITY_14,
        tooltip: '',
      };
    case EBasicField.STATUS:
      return {
        id: EBasicField.STATUS,
        type: EAdvancedFilterType.STATUS,
        iconSrc: EIconPath.ICON_ACTIVITIES_STATUS_14,
        tooltip: '',
      };
    case EBasicField.TITLE:
      return {
        id: EBasicField.TITLE,
        type: EAdvancedFilterType.TITLE,
        iconSrc: EIconPath.ICON_FIELDS_TEXT_14,
        tooltip: '',
      };
    case EBasicField.DESCRIPTION:
      return {
        id: EBasicField.DESCRIPTION,
        type: EAdvancedFilterType.TEXT,
        iconSrc: EIconPath.ICON_FIELDS_TEXT_14,
        tooltip: '',
      };
    case EBasicField.CREATED:
      return {
        id: EBasicField.CREATED,
        type: EAdvancedFilterType.DATE_CREATED,
        iconSrc: EIconPath.ICON_FIELDS_DATE_14,
        tooltip: '',
      };
    case EBasicField.SEQUENCE_NUMBER:
    case EBasicFieldShort.SEQUENCE_NUMBER:
      return {
        id: EBasicField.SEQUENCE_NUMBER,
        type: EAdvancedFilterType.SEQUENCE_NUMBER,
        iconSrc: EIconPath.ICON_FIELDS_NUMBERS_14,
        tooltip: '',
      };
    case EBasicField.UPDATED:
      return {
        id: EBasicField.UPDATED,
        type: EAdvancedFilterType.DATE_UPDATED,
        iconSrc: EIconPath.ICON_FIELDS_DATE_14,
        tooltip: '',
      };
    case EBasicField.CREATED_BY:
      return {
        id: EBasicField.CREATED_BY,
        type: EAdvancedFilterType.CREATED_BY,
        iconSrc: EIconPath.ICON_ACTIVITIES_USER_14,
        tooltip: '',
      };
    case EBasicField.ASSIGNEES:
      return {
        id: EBasicField.ASSIGNEES,
        type: EAdvancedFilterType.ASSIGNEE,
        iconSrc: EIconPath.ICON_ACTIVITIES_USER_14,
        tooltip: '',
      };
    case EBasicField.TAGS:
      return {
        id: EBasicField.TAGS,
        type: EAdvancedFilterType.TAG,
        iconSrc: EIconPath.ICON_ACTIVITIES_TAGS_14,
        tooltip: '',
      };
    case EAdvancedFilterType.LOCATION:
      return {
        id: EAdvancedFilterType.LOCATION,
        type: EAdvancedFilterType.LOCATION,
        iconSrc: EIconPath.ICON_ACTIVITIES_LOCATION_14,
        tooltip: '',
      };
    case EAdvancedFilterType.REMINDER:
      return {
        id: EAdvancedFilterType.REMINDER,
        type: EAdvancedFilterType.REMINDER,
        iconSrc: EIconPath.ICON_FIELDS_DATE_14,
        tooltip: '',
      };
    case EAdvancedFilterType.REMINDER_TYPE:
      return {
        id: EAdvancedFilterType.REMINDER_TYPE,
        type: EAdvancedFilterType.REMINDER_TYPE,
        iconSrc: EIconPath.ICON_MISC_REMINDER_ICON_14,
        tooltip: '',
      };
    case EAdvancedFilterType.REMINDER_STATE:
      return {
        id: EAdvancedFilterType.REMINDER_STATE,
        type: EAdvancedFilterType.REMINDER_STATE,
        iconSrc: EIconPath.ICON_MISC_REMINDER_ICON_14,
        tooltip: '',
      };
    case EAdvancedFilterType.REMINDER_CREATED_BY:
      return {
        id: EAdvancedFilterType.REMINDER_CREATED_BY,
        type: EAdvancedFilterType.REMINDER_CREATED_BY,
        iconSrc: EIconPath.ICON_MISC_REMINDER_ICON_14,
        tooltip: '',
      };
    case EBasicField.FLAGGED:
    case EBasicFieldShort.FLAGGED:
      return {
        id: EAdvancedFilterType.FLAGGED,
        type: EAdvancedFilterType.FLAGGED,
        iconSrc: EIconPath.ICON_ACTIVITIES_RED_FLAG_14,
        tooltip: '',
      };
    default: {
      const customFields = GET_CUSTOM_FIELDS();

      const field = Object.values(customFields).find(
        (item) => item.label === newValue && customFieldIds.includes(item.id),
      );

      if (!field) {
        return null;
      }

      const { type, iconSrc } = getFilterType(field.type as ECustomFieldType);

      const volyIntegrationState = field.volyIntegrationActive;

      const icon =
        volyIntegrationState === EIntegrationStatus.DISABLED
          ? EIconPath.ICON_INTEGRATION_VOLY_INACTIVE_SMALL
          : volyIntegrationState === EIntegrationStatus.ON
            ? EIconPath.ICON_INTEGRATION_VOLY_ACTIVE_SMALL
            : iconSrc;

      const tooltip =
        volyIntegrationState === EIntegrationStatus.DISABLED
          ? translate('custom_field_voly_disabled_tooltip')
          : volyIntegrationState === EIntegrationStatus.ON
            ? translate('custom_field_voly_tooltip')
            : '';

      return {
        id: field.id,
        type,
        iconSrc: icon,
        tooltip,
      };
    }
  }
}
